import { useCallback, useState } from 'react';

import { Apollo } from 'src/gql/generated';
import { useLogAndDispatchErrorSnack } from 'src/helpers';
import { getCanvasBlob } from 'src/layout-engine/components/dialogs/signature-canvas-utils';
import { Sentry } from 'src/sentry';
import { v4 as uuid4 } from 'uuid';

export interface AdoptedSignature {
  /**
   * PNG data URL of the signature canvas, captured client-side before the
   * upsert. PDF stamping needs the raw bytes immediately, so we skip waiting
   * for the post-upsert filestack round-trip.
   */
  b64Url: string;
  // Server-assigned id for the saved UserSignature row.
  id: string;
}

interface AdoptArgs {
  initialsCanvas: HTMLCanvasElement;
  signatureCanvas: HTMLCanvasElement;
}

interface UseAdoptUserSignaturesResult {
  /**
   * Resolves to the saved signature on success, `null` if the upsert failed
   * (the failure is already reported via snackbar + Sentry inside the hook).
   */
  adopt: (args: AdoptArgs) => Promise<AdoptedSignature | null>;
  submitting: boolean;
}

/**
 * Shared canvas→blob→File→`upsertUserSignatures` pipeline used by the mobile
 * adoption sheet and the desktop adoption dialog. Captures the signature data
 * URL before the async hop so callers (PDF stamping) can apply the signature
 * without waiting on the post-upsert filestack fetch.
 */
export function useAdoptUserSignatures(): UseAdoptUserSignaturesResult {
  const [upsertUserSignatures] = Apollo.useUpsertUserSignaturesMutation();
  const [submitting, setSubmitting] = useState(false);
  const dispatchErrorNotification = useLogAndDispatchErrorSnack({
    message: 'An error occurred capturing signatures.',
  });

  const adopt = useCallback(
    async ({ initialsCanvas, signatureCanvas }: AdoptArgs): Promise<AdoptedSignature | null> => {
      setSubmitting(true);
      try {
        const b64Url = signatureCanvas.toDataURL('image/png');
        const [signatureBlob, initialsBlob] = await Promise.all([
          getCanvasBlob(signatureCanvas),
          getCanvasBlob(initialsCanvas),
        ]);
        if (!signatureBlob || !initialsBlob) throw new Error('Canvas failed to return blob.');

        const result = await upsertUserSignatures({
          variables: {
            input: {
              initialsFile: new File([initialsBlob], `user-initials-${uuid4()}`),
              signatureFile: new File([signatureBlob], `user-signature-${uuid4()}`),
            },
          },
        });
        const id = result.data?.upsertUserSignatures.settings.signatureImg?.id ?? null;
        if (!id) throw new Error('Adoption did not return a signature id.');

        return { b64Url, id };
      } catch (err) {
        Sentry.captureException(err);
        dispatchErrorNotification(err);
        return null;
      } finally {
        setSubmitting(false);
      }
    },
    [dispatchErrorNotification, upsertUserSignatures]
  );

  return { adopt, submitting };
}
